import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';
import { useAuth } from './AuthContext';
import { useAPI } from './APIContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { clearReferralId } from '../hooks/useReferralCapture';
import { showToast } from '../components/Toast';
import { saveCreationDraft } from '../utils/creationDraft';
import { isFunnelPath } from '../utils/funnelPaths';
import { saveBuddyCharacter } from '../utils/buddyCharacter';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { user, isAuthenticated, updateUser, subscriptionStatus, hasEntitlement, isTrialing, isGrandfathered } = useAuth();
  const { apiClient } = useAPI();
  const { trackOnboardingStep, setUserProperties } = useAnalytics();

  // DISABLE ONBOARDING: Set to true to re-enable onboarding flow
  const ONBOARDING_ENABLED = true;
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    // Try to restore step from localStorage (for when user returns from Stripe)
    const savedStep = localStorage.getItem('pixley_onboarding_step');
    const parsedStep = savedStep ? parseInt(savedStep, 10) : 1;

    if (parsedStep < 1 || parsedStep > 10 || isNaN(parsedStep)) {
      logger.warn('🚨 Invalid onboarding step found in localStorage:', savedStep, 'Resetting to step 1');
      // Clear invalid localStorage data
      localStorage.removeItem('pixley_onboarding_step');
      localStorage.removeItem('pixley_onboarding_active');
      return 1;
    }

    logger.log('🔄 Restored onboarding step from localStorage:', parsedStep);
    return parsedStep;
  });
  const [characterName, setCharacterName] = useState('');
  const [username, setUsername] = useState('');
  const [selectedShows, setSelectedShows] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [originalProfileImage, setOriginalProfileImage] = useState(null); // Store original image before animation
  // A ready-made buddy (Zippy/Benny/Pip) picked instead of a photo:
  // { name, src }. completeOnboarding saves it to the account as the child's
  // first character, the way a photo (and the funnel's preset friend) is.
  const [avatarBuddy, setAvatarBuddy] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // NEW state for additional steps
  const [childAge, setChildAge] = useState('');
  const [discoverySource, setDiscoverySource] = useState([]);
  const [goals, setGoals] = useState([]);
  // iOS-mirrored steps (2026-08 rework)
  const [screenTime, setScreenTime] = useState('youtube');
  const [adventure, setAdventure] = useState(null);
  // Avatar animation runs in the BACKGROUND (iOS pattern): it kicks off when
  // the photo is confirmed and the parent keeps answering steps; the Meet
  // reveal waits on avatarPending only if it's still running by then.
  const [avatarPending, setAvatarPending] = useState(false);

  // Analytics tracking state
  const [stepStartTime, setStepStartTime] = useState(null);
  const [onboardingStartTime, setOnboardingStartTime] = useState(null);

  // iOS-mirrored flow: 1. Screen time, 2. Name+Age, 3. Avatar,
  // 4. Interests, 5. Discovery source, 6. Meet, 7. First adventure.
  // Step 8 (not counted) is the in-flow mandatory paywall.
  const totalSteps = 7;
  const PAYWALL_STEP = 8;
  // True once completeOnboarding lands on the paywall step — stops the
  // persona-based trigger effect from closing the flow when updateUser
  // delivers the freshly-written persona.
  const completedIntoPaywallRef = useRef(false);

  logger.log('🟡 OnboardingContext: State', {
    currentStep,
    totalSteps,
    showOnboarding
  });

  // Check if user should see onboarding
  useEffect(() => {
    // Dev-only design preview: `localStorage.pixley_onboarding_preview = 'true'`
    // forces the flow open without an account (steps that call the API will
    // no-op). Stripped from production builds by the NODE_ENV guard.
    if (
      process.env.NODE_ENV === 'development' &&
      localStorage.getItem('pixley_onboarding_preview') === 'true'
    ) {
      setShowOnboarding(true);
      return;
    }

    if (!ONBOARDING_ENABLED) {
      logger.log('🚫 Onboarding is disabled');
      setShowOnboarding(false);
      return;
    }

    if (isAuthenticated && user) {
      // A RevenueCat web-funnel sign-in is mid-flight — Google's OAuth now lands
      // straight back on /funnel-auth, so that route is the signal (same one
      // AuthContext uses). Don't hijack the render with the onboarding modal:
      // AppLayout returns ONLY <OnboardingModal /> when showOnboarding is true,
      // which unmounts the routes and stops FunnelAuthPage from finishing the
      // hand-off (it writes the persona AND redirects back to the RC paywall in
      // completeAndRedirect). Defer to the funnel — it marks the account
      // onboarded via persona, so onboarding won't re-trigger afterward.
      // /funnel is the funnel's video welcome page — same deal: it must stay
      // mounted so its CTA can carry redirect_uri/state into /funnel-auth.
      // (Every entry, the intro one included — utils/funnelPaths.)
      if (isFunnelPath(window.location.pathname)) {
        logger.log('↩️ Funnel sign-in active — deferring onboarding to the funnel flow');
        setShowOnboarding(false);
        return;
      }

      const isNewUser = checkIfNewUser(user);

      logger.log('🔍 Onboarding check:', {
        isNewUser,
        userPersona: user.persona
      });

      // ONLY show onboarding if persona is empty - nothing else matters
      if (isNewUser) {
        // Check if onboarding is already in progress (user is mid-flow)
        const onboardingActive = localStorage.getItem('pixley_onboarding_active');

        if (onboardingActive === 'true') {
          // Onboarding already started - don't reset, just show it
          logger.log('🔄 Onboarding already in progress, resuming at step:', currentStep);
          setShowOnboarding(true);

          // Track resumption
          trackOnboardingStep('onboarding', 'resumed', {
            resumed_at_step: currentStep,
            total_steps: totalSteps
          });
        } else {
          // Fresh start - reset everything to step 1
          logger.log('🚀 Starting fresh onboarding - persona is empty');
          localStorage.removeItem('pixley_onboarding_step');
          localStorage.removeItem('pixley_onboarding_active');
          setCurrentStep(1);
          setShowOnboarding(true);
          localStorage.setItem('pixley_onboarding_active', 'true');
          localStorage.setItem('pixley_onboarding_step', '1');

          // Track onboarding started
          const startTime = Date.now();
          setOnboardingStartTime(startTime);
          setStepStartTime(startTime);

          trackOnboardingStep('onboarding', 'started', {
            total_steps: totalSteps,
            user_id: user?.id
          });

          trackOnboardingStep('username', 'started');

          // Save initial analytics to Supabase
          saveOnboardingAnalytics({
            status: 'in_progress',
            started_at: new Date().toISOString(),
            current_step: 1,
            stopped_at_step: null,
            stopped_at_step_number: null,
            steps_completed: {}
          });
        }
      } else if (completedIntoPaywallRef.current) {
        // Just finished onboarding into the in-flow paywall — persona now
        // exists, but the flow stays up until checkout navigates away.
        logger.log('💳 Persona saved; holding onboarding open on the paywall step');
      } else {
        logger.log('✅ Persona has data, skipping onboarding');
        setShowOnboarding(false);
      }
    }
  }, [isAuthenticated, user, ONBOARDING_ENABLED]); // Removed currentStep to prevent reset loop

  // Track abandonment when user leaves onboarding mid-flow
  useEffect(() => {
    if (!showOnboarding) return;

    const handleBeforeUnload = () => {
      // Only track abandonment if onboarding is still active (not completed)
      const onboardingActive = localStorage.getItem('pixley_onboarding_active');
      if (onboardingActive === 'true') {
        const stepNames = ['', 'screen_time', 'name_age', 'avatar', 'interests', 'discovery_source', 'meet', 'adventure'];
        const timeSpentOnStep = stepStartTime ? Math.round((Date.now() - stepStartTime) / 1000) : null;
        const totalTimeInOnboarding = onboardingStartTime ? Math.round((Date.now() - onboardingStartTime) / 1000) : null;

        trackOnboardingStep('onboarding', 'abandoned', {
          abandoned_at_step: currentStep,
          step_name: stepNames[currentStep] || 'unknown',
          time_spent_on_step_seconds: timeSpentOnStep,
          total_time_in_onboarding_seconds: totalTimeInOnboarding,
          total_steps: totalSteps,
          completion_percentage: Math.round((currentStep / totalSteps) * 100)
        });

        // Save abandonment analytics to Supabase
        saveOnboardingAnalytics({
          status: 'abandoned',
          stopped_at_step: stepNames[currentStep] || 'unknown',
          stopped_at_step_number: currentStep,
          total_time_seconds: totalTimeInOnboarding,
          current_step: currentStep,
          completion_percentage: Math.round((currentStep / totalSteps) * 100)
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showOnboarding, currentStep, stepStartTime, onboardingStartTime, totalSteps, trackOnboardingStep]);

  // Check if user needs onboarding - only trigger when persona is null or empty
  const checkIfNewUser = (user) => {
    if (!user) {
      logger.log('🔍 No user found');
      return false;
    }

    // Parse persona if it's a JSON string
    let persona = user.persona;
    if (typeof persona === 'string') {
      try {
        persona = JSON.parse(persona);
        logger.log('🔄 Parsed persona from JSON string:', persona);
      } catch (e) {
        logger.warn('⚠️ Failed to parse persona string:', persona);
        // If it's a non-empty string that can't be parsed, consider it as having persona data
        return !persona || persona.trim() === '' || persona === '{}';
      }
    }

    // Only show onboarding if persona is null, undefined, empty object, or not an object
    const isNewUser = !persona ||
                      typeof persona !== 'object' ||
                      Array.isArray(persona) ||
                      Object.keys(persona).length === 0;

    logger.log('🔍 Persona check:', {
      persona: persona,
      isNull: persona === null,
      isUndefined: persona === undefined,
      isEmpty: persona && Object.keys(persona).length === 0,
      shouldOnboard: isNewUser
    });

    // Trigger onboarding only if persona is null or completely empty
    return isNewUser;
  };

  // Save onboarding analytics to backend
  const saveOnboardingAnalytics = async (analyticsData) => {
    if (!user?.id || !apiClient) {
      logger.warn('⚠️ Cannot save analytics: missing user or API client');
      return;
    }

    try {
      logger.log('💾 Saving onboarding analytics to backend:', analyticsData);
      const response = await apiClient.updateUserAnalytics(user.id, analyticsData, 'onboarding');

      if (response.status === 'success' || response.success) {
        logger.log('✅ Onboarding analytics saved successfully');
      } else {
        logger.warn('⚠️ Failed to save onboarding analytics:', response.error || response.message);
      }
    } catch (error) {
      logger.error('❌ Error saving onboarding analytics:', error);
    }
  };

  // Navigate to next step
  const nextStep = () => {
    logger.log('➡️ nextStep called:', { currentStep, totalSteps });

    if (currentStep < totalSteps) {
      const newStep = currentStep + 1;
      logger.log('✅ Moving to step:', newStep);

      // Calculate time spent on current step
      const timeSpentOnStep = stepStartTime ? Math.round((Date.now() - stepStartTime) / 1000) : null;

      // Track step completion with time spent
      const stepNames = ['', 'screen_time', 'name_age', 'avatar', 'interests', 'discovery_source', 'meet', 'adventure'];
      if (stepNames[currentStep]) {
        trackOnboardingStep(stepNames[currentStep], 'completed', {
          time_spent_seconds: timeSpentOnStep,
          step_number: currentStep,
          total_steps: totalSteps
        });
      }

      // Reset step start time for next step
      setStepStartTime(Date.now());

      // Track next step started
      if (stepNames[newStep]) {
        trackOnboardingStep(stepNames[newStep], 'started', {
          step_number: newStep,
          total_steps: totalSteps
        });
      }

      // Update current step in Supabase analytics
      saveOnboardingAnalytics({
        current_step: newStep,
        status: 'in_progress'
      });

      setCurrentStep(newStep);
      localStorage.setItem('pixley_onboarding_step', newStep.toString());
      localStorage.setItem('pixley_onboarding_active', 'true');
    } else {
      logger.warn('⚠️ Already at final step, cannot go further');
    }
  };

  // Navigate to previous step
  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      localStorage.setItem('pixley_onboarding_step', newStep.toString());
      localStorage.setItem('pixley_onboarding_active', 'true');
    }
  };

  // Go to specific step
  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      localStorage.setItem('pixley_onboarding_step', step.toString());
      localStorage.setItem('pixley_onboarding_active', 'true');
    }
  };

  // Start onboarding (for testing/debug)
  const startOnboarding = () => {
    logger.log('🚀 Manually starting onboarding');
    setShowOnboarding(true);
    setCurrentStep(1);
    setCharacterName('');
    setUsername('');
    localStorage.setItem('pixley_onboarding_step', '1');
    localStorage.setItem('pixley_onboarding_active', 'true');
  };

  // Complete onboarding
  const startAvatarAnimation = (file) => {
    setAvatarPending(true);
    (async () => {
      try {
        const resp = await apiClient.animateProfilePicture(file);
        const url = resp?.data?.image_url || resp?.image_url;
        if (resp?.isSafetyBlock || resp?.error === 'SAFETY_BLOCK') {
          showToast("That photo isn't suitable for kids' stories — we'll use a default avatar instead.", 'error', 6000);
          setProfilePicture(null);
        } else if (resp?.status && url) {
          setProfilePicture(url);
        } else {
          logger.warn('Avatar animation returned no image');
          setProfilePicture(null);
        }
      } catch (e) {
        logger.error('Avatar animation failed', e);
        setProfilePicture(null);
      } finally {
        setAvatarPending(false);
      }
    })();
  };

  const completeOnboarding = async (adventureOverride = null) => {
    const firstAdventure = adventureOverride || adventure;
    logger.log('✅ Completing onboarding');
    setIsLoading(true);

    try {
      if (!user || !apiClient) {
        logger.error('❌ No user or API client available');
        throw new Error('User or API client not available');
      }

      // 1. Save username if provided
      if (username) {
        logger.log('💾 Saving username:', username);
        const usernameResponse = await apiClient.updateUsername(user.id, username);

        if (usernameResponse.status) {
          logger.log('✅ Username saved successfully');
        } else {
          logger.warn('⚠️ Failed to save username:', usernameResponse.error);
        }
      }

      // 2. Save profile picture to users table
      if (profilePicture) {
        logger.log('💾 Saving profile picture to users table:', profilePicture);
        const profilePictureResponse = await apiClient.updateProfilePicture(user.id, profilePicture);

        if (profilePictureResponse.status) {
          logger.log('✅ Profile picture saved successfully');
        } else {
          logger.warn('⚠️ Failed to save profile picture:', profilePictureResponse.error);
        }

        // Sync the default profile's avatar too — the nav renders
        // currentProfile.avatar_url, which nothing in onboarding set before
        // (users saw a stale/placeholder avatar top-right after onboarding).
        if (!profilePicture.startsWith('default:')) {
          try {
            const defProfile = await apiClient.getDefaultProfile?.();
            const profileId = defProfile?.data?.id || defProfile?.id;
            if (profileId) {
              await apiClient.updateProfile(profileId, { avatarUrl: profilePicture });
              logger.log('✅ Default profile avatar synced');
            }
          } catch (e) {
            logger.warn('⚠️ Could not sync profile avatar:', e);
          }
        }
      }

      // 3. Auto-create first character from animated profile picture
      if (profilePicture && !profilePicture.startsWith('default:') && originalProfileImage) {
        logger.log('🎨 Creating first character from profile picture');
        logger.log('Original image (handdrawn):', originalProfileImage ? 'exists' : 'missing');
        logger.log('Animated image:', profilePicture);

        const characterName = username || user.username || user.email?.split('@')[0] || 'My Character';

        // Helper function to convert data URL to File
        const dataURLtoFile = (dataurl, filename) => {
          const arr = dataurl.split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new File([u8arr], filename, { type: mime });
        };

        // Convert original image (before animation) to File
        const handdrawnFile = dataURLtoFile(originalProfileImage, 'handdrawn.jpg');

        // Fetch and convert animated image URL to File
        const animatedResponse = await fetch(profilePicture);
        const animatedBlob = await animatedResponse.blob();
        const animatedFile = new File([animatedBlob], 'animated.png', { type: animatedBlob.type });

        const characterData = {
          user_id: user.id,
          name: characterName,
          handdrawn_image: handdrawnFile,
          animated_image: animatedFile,
        };

        logger.log('💾 Creating character with both images');
        const characterResponse = await apiClient.createCharacterFromImages(characterData);

        if (characterResponse.status) {
          logger.log('✅ First character created successfully:', characterResponse);
        } else {
          logger.warn('⚠️ Failed to create character:', characterResponse.error);
        }
      } else if (avatarBuddy?.src && profilePicture && !profilePicture.startsWith('default:')) {
        // 3b. A buddy pick is kept exactly as the funnel keeps a preset
        // friend (utils/buddyCharacter): the child's first character, named
        // after the child, with the buddy's picture — so the account owns a
        // character and the first story can queue after payment. Best
        // effort, never blocks finishing, like 3.
        const characterName = username || user.username || user.email?.split('@')[0] || 'My Character';
        try {
          await saveBuddyCharacter(apiClient, { userId: user.id, name: characterName, src: avatarBuddy.src });
          logger.log('✅ Buddy saved as first character:', avatarBuddy.name);
        } catch (e) {
          logger.warn('⚠️ Failed to save buddy character (non-blocking):', e);
        }
      }

      // 4. Save persona (shows, interests, profile picture, and new fields)
      const personaData = {
        favorite_shows: selectedShows,
        aggregated_topics: [],
        aggregated_interests: selectedInterests,
        aggregated_preferences: [],
        profile_picture: profilePicture || null,
        child_age: childAge,
        discovery_source: discoverySource,
        goals: goals,
        screen_time_replacing: screenTime || null,
        first_adventure: firstAdventure || null
      };

      logger.log('💾 Saving persona:', personaData);
      const personaResponse = await apiClient.updateUserPersona(user.id, personaData);

      if (!personaResponse.status) {
        logger.error('❌ Failed to save persona:', personaResponse.error);
        throw new Error('Failed to save your preferences. Please try again.');
      }

      logger.log('✅ Persona saved to backend');

      // Verify persona was actually saved by fetching fresh data from backend
      logger.log('🔍 Verifying persona save...');
      const sessionResult = await apiClient.getSession();

      if (!sessionResult.status || !sessionResult.data?.user) {
        logger.error('❌ Failed to verify persona save - could not fetch session');
        throw new Error('Could not verify your preferences were saved. Please try again.');
      }

      const freshUser = sessionResult.data.user;
      logger.log('📦 Fresh user data from backend:', freshUser);

      // Check if persona exists and has data
      let verifiedPersona = freshUser.persona;
      if (typeof verifiedPersona === 'string') {
        try {
          verifiedPersona = JSON.parse(verifiedPersona);
        } catch (e) {
          logger.error('❌ Could not parse persona from backend');
          throw new Error('Your preferences were saved incorrectly. Please try again.');
        }
      }

      if (!verifiedPersona || typeof verifiedPersona !== 'object' || Object.keys(verifiedPersona).length === 0) {
        logger.error('❌ Persona verification failed - no persona in backend data');
        throw new Error('Your preferences were not saved properly. Please try again.');
      }

      logger.log('✅ Persona VERIFIED - data is persisted in backend');

      // Update user context with verified backend data
      updateUser(freshUser);

      // Track onboarding completion with total time
      const totalOnboardingTime = onboardingStartTime ? Math.round((Date.now() - onboardingStartTime) / 1000) : null;

      trackOnboardingStep('storyverse_feature', 'completed');
      trackOnboardingStep('onboarding', 'completed', {
        hasUsername: !!username,
        hasProfilePicture: !!profilePicture,
        childAge: childAge,
        discoverySourceCount: discoverySource.length,
        selectedShowsCount: selectedShows.length,
        selectedInterestsCount: selectedInterests.length,
        goalsCount: goals.length,
        totalSteps: totalSteps,
        total_onboarding_time_seconds: totalOnboardingTime,
        total_onboarding_time_minutes: totalOnboardingTime ? Math.round(totalOnboardingTime / 60) : null
      });

      // Enrich PostHog person properties with onboarding data
      setUserProperties({
        child_age_range: childAge || null,
        discovery_source: discoverySource.length > 0 ? discoverySource.join(', ') : null,
        onboarding_completed: true,
        activation_status: 'activated',
      });

      // Save completion analytics to Supabase
      saveOnboardingAnalytics({
        status: 'completed',
        completed_at: new Date().toISOString(),
        total_time_seconds: totalOnboardingTime,
        current_step: totalSteps,
        stopped_at_step: null,
        stopped_at_step_number: null,
        steps_completed: {
          username: { completed: !!username },
          avatar: { completed: !!profilePicture },
          child_age: { completed: !!childAge },
          discovery_source: { completed: discoverySource.length > 0 },
          shows: { completed: selectedShows.length > 0 },
          interests: { completed: selectedInterests.length > 0 },
          goals: { completed: goals.length > 0 }
        }
      });

      // Clear onboarding localStorage data
      localStorage.removeItem('pixley_onboarding_step');
      localStorage.removeItem('pixley_onboarding_active');
      
      // Clear referral ID after successful onboarding (referral already completed by backend)
      clearReferralId();

      // First-story handoff: prefill the Create Story page with the
      // adventure they picked (restored by CreateStory's draft hooks) and
      // land there after the reload — the TrialWall CTA pops on top.
      if (firstAdventure) {
        const storyIdea = firstAdventure.lesson
          ? `A story that teaches ${username || 'my child'} about ${firstAdventure.lesson}`
          : `${firstAdventure.theme_title} adventure starring ${username || 'my child'}`;
        try {
          // EntitlementGateContext auto-queues this as their first story the
          // moment they're entitled + past the consent gate (iOS parity).
          // The one-slot /create draft stays as the manual fallback.
          localStorage.setItem(
            'pixley_first_story_pending',
            JSON.stringify({ idea: storyIdea, savedAt: Date.now() })
          );
          saveCreationDraft('create-story', { storyDescription: storyIdea });
        } catch (e) {
          logger.warn('Could not stash first-story draft', e);
        }
      }

      // Reload fallback: if they bail before paying, Navigation re-opens the
      // wall on the next load (flag persists until entitled under the
      // cutover; one-shot legacy modal otherwise).
      localStorage.setItem('pixley_show_subscription_cta', 'true');

      const alreadyEntitled = hasEntitlement || isTrialing || isGrandfathered;
      if (alreadyEntitled) {
        // Gifted / comped / already-subscribed families never see the wall.
        // (Gift redemption grants an entitlement before onboarding ends.)
        localStorage.removeItem('pixley_show_subscription_cta');
      }
      if (subscriptionStatus?.subscriptionRequired === true && !alreadyEntitled) {
        // Cutover live: no modal, no navigation, no reload — the paywall IS
        // the next onboarding step; the story loader resolves straight into it.
        completedIntoPaywallRef.current = true;
        setCurrentStep(PAYWALL_STEP);
      } else {
        // Pre-cutover (deploy-order safety): close the flow the legacy way so
        // shipping this client before the backend flag flips never strands
        // new users on a paywall with no trial behind it.
        setShowOnboarding(false);
        setCurrentStep(1);
        setTimeout(() => {
          const postNav = localStorage.getItem('pixley_post_onboarding_nav');
          if (postNav) {
            localStorage.removeItem('pixley_post_onboarding_nav');
            window.location.href = postNav;
          } else {
            window.location.reload();
          }
        }, 300);
      }

    } catch (error) {
      logger.error('❌ Error completing onboarding:', error);

      // Show error to user - don't complete onboarding, let them retry
      const errorMessage = error.message || 'Failed to complete onboarding. Please try again.';
      showToast(errorMessage, 'error');

      // Keep them in onboarding (don't clear flags or navigate)
      // They can retry by clicking the button again
      setIsLoading(false);
    } finally {
      // Only set loading false if not already handled in catch
      if (isLoading) {
        setIsLoading(false);
      }
    }
  };

  // Skip onboarding
  const skipOnboarding = () => {
    logger.log('⏭️ Skipping onboarding');

    // Track skip with context
    const stepNames = ['', 'screen_time', 'name_age', 'avatar', 'interests', 'discovery_source', 'meet', 'adventure'];
    const timeSpentOnStep = stepStartTime ? Math.round((Date.now() - stepStartTime) / 1000) : null;
    const totalTimeInOnboarding = onboardingStartTime ? Math.round((Date.now() - onboardingStartTime) / 1000) : null;

    trackOnboardingStep('onboarding', 'skipped', {
      skipped_at_step: currentStep,
      step_name: stepNames[currentStep] || 'unknown',
      time_spent_on_step_seconds: timeSpentOnStep,
      total_time_in_onboarding_seconds: totalTimeInOnboarding,
      completion_percentage: Math.round((currentStep / totalSteps) * 100),
      total_steps: totalSteps
    });

    // Save skip analytics to Supabase
    saveOnboardingAnalytics({
      status: 'skipped',
      stopped_at_step: stepNames[currentStep] || 'unknown',
      stopped_at_step_number: currentStep,
      total_time_seconds: totalTimeInOnboarding,
      current_step: currentStep,
      completion_percentage: Math.round((currentStep / totalSteps) * 100)
    });

    // Clear onboarding localStorage data
    localStorage.removeItem('pixley_onboarding_step');
    localStorage.removeItem('pixley_onboarding_active');

    setShowOnboarding(false);
    setCurrentStep(1);
    setCharacterName('');
    setUsername('');
    showToast('You can always explore Pixley features from the home page!', 'info');
  };

  const value = {
    // State
    showOnboarding,
    currentStep,
    totalSteps,
    characterName,
    username,
    selectedShows,
    selectedInterests,
    profilePicture,
    originalProfileImage,
    avatarBuddy,
    isLoading,
    childAge,
    discoverySource,
    goals,
    screenTime,
    adventure,
    avatarPending,

    // Actions
    setShowOnboarding,
    setCharacterName,
    setUsername,
    setSelectedShows,
    setSelectedInterests,
    setProfilePicture,
    setOriginalProfileImage,
    setAvatarBuddy,
    setChildAge,
    setDiscoverySource,
    setGoals,
    setScreenTime,
    setAdventure,
    startAvatarAnimation,
    nextStep,
    prevStep,
    goToStep,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,

    // Computed
    progress: (currentStep / totalSteps) * 100,
    isFirstStep: currentStep === 1,
    isLastStep: currentStep === totalSteps,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};